import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Heart, CheckCircle, Repeat } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { paymentLinks } from '../paymentLinks';

const DonationFlow = () => {
  const { sevaType } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Details, 2: Confirmation, 3: Success
  const [donationData, setDonationData] = useState({
    amount: '',
    isRecurring: false
  });

  const seva = location.state?.seva;
  const isSubscription = location.state?.isSubscription || false;

  // Mock seva data if not found
  const mockSeva = {
    id: sevaType,
    title: 'General Seva',
    amount: 51,
    description: 'Support our general preaching activities'
  };

  const currentSeva = seva || mockSeva;

  React.useEffect(() => {
    const baseAmount = currentSeva.amount ? currentSeva.amount.toString() : '';
    setDonationData({
      amount: isSubscription ? '51' : baseAmount,
      isRecurring: !!isSubscription
    });
  }, [isSubscription, currentSeva.amount]);

  const handleInputChange = (field, value) => {
    setDonationData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!donationData.amount) {
      toast({
        title: "Amount required",
        description: "Please enter a donation amount.",
        variant: "destructive"
      });
      return;
    }

    // Determine correct Stripe payment link
    const sevaKey = currentSeva.id || sevaType;
    const frequency = donationData.isRecurring ? 'recurring' : 'oneTime';
    const payUrl =
      paymentLinks[sevaKey]?.[frequency] ||
      paymentLinks.prabhupada?.[frequency];

    if (!payUrl) {
      toast({
        title: "Payment link unavailable",
        description: "We couldn't find a payment link for this selection. Please contact the temple or try another seva.",
        variant: "destructive"
      });
      return;
    }

    // Open Stripe checkout in a new tab
    window.open(payUrl, '_blank', 'noopener,noreferrer');

    // Show processing state then thank-you page
    setStep(2);
    setTimeout(() => {
      const params = new URLSearchParams({
        amount: donationData.amount,
        seva: currentSeva.title,
        recurring: donationData.isRecurring.toString()
      });
      navigate(`/thank-you?${params.toString()}`);
    }, 1500);
  };

  const resetForm = () => {
    setStep(1);
    setDonationData({
      amount: isSubscription ? '51' : (currentSeva.amount?.toString() || ''),
      isRecurring: isSubscription
    });
  };

  if (step === 3) {
    return (
      <div className="donation-success">
        <div className="container">
          <Card className="success-card">
            <CardContent className="success-content">
              <CheckCircle className="success-icon" />
              <h2 className="success-title">Donation Successful!</h2>
              <p className="success-message">
                Thank you for your generous {donationData.isRecurring ? 'monthly ' : ''}
                donation of ${donationData.amount} to {currentSeva.title}.
              </p>
              {donationData.isRecurring && (
                <p className="recurring-note">
                  Your monthly subscription is now active. Please collect Mahaprasad from the gift shop whenever you visit the temple.
                </p>
              )}
              <div className="success-actions">
                <Button onClick={() => navigate('/')} className="primary-btn">
                  Return to Home
                </Button>
                <Button variant="outline" onClick={resetForm}>
                  Make Another Donation
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="donation-flow">
      <div className="container">
        <div className="flow-header">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="flow-title">
            {isSubscription ? 'Monthly Subscription' : 'Complete Your Donation'}
          </h1>
        </div>

        <div className="flow-content">
          <div className="donation-summary">
            <Card className="summary-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {isSubscription ? <Repeat className="w-5 h-5" /> : <Heart className="w-5 h-5" />}
                  {currentSeva.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="seva-description">{currentSeva.description}</p>
                <div className="amount-display">
                  <Badge variant="secondary" className="amount-badge">
                    ${donationData.amount} {donationData.isRecurring ? 'monthly' : 'one-time'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="donation-form-section">
            {step === 1 && (
              <Card className="form-card">
                <CardHeader>
                  <CardTitle>Donation Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="donation-form">
                    <div className="form-row">
                      <div className="form-group">
                        <Label htmlFor="amount">Amount ($)</Label>
                        <Input
                          id="amount"
                          type="number"
                          min="1"
                          value={donationData.amount}
                          onChange={(e) => handleInputChange('amount', e.target.value)}
                          placeholder="Enter amount"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <Label>Donation Type</Label>
                        <div className="donation-type-options">
                          <div className="option-row">
                            <input
                              type="radio"
                              id="one-time"
                              name="donationType"
                              checked={!donationData.isRecurring}
                              onChange={() => handleInputChange('isRecurring', false)}
                            />
                            <Label htmlFor="one-time" className="option-label">One-time donation</Label>
                          </div>
                          <div className="option-row">
                            <input
                              type="radio"
                              id="monthly"
                              name="donationType"
                              checked={donationData.isRecurring}
                              onChange={() => handleInputChange('isRecurring', true)}
                            />
                            <Label htmlFor="monthly" className="option-label">Monthly subscription</Label>
                          </div>
                          {donationData.isRecurring && (
                            <p className="subscription-note">
                              Please collect Mahaprasad from the gift shop whenever you visit the temple.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <Button type="submit" className="submit-btn" size="lg">
                      {isSubscription ? 'Start Subscription' : 'Proceed to Payment'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}

            {step === 2 && (
              <Card className="processing-card">
                <CardContent className="processing-content">
                  <div className="processing-spinner"></div>
                  <h3>Processing Your Donation...</h3>
                  <p>Please wait while we process your {donationData.isRecurring ? 'subscription' : 'donation'}.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationFlow;
